import kebabCase from "lodash/kebabCase";
import { useCallback, useEffect, useState } from "react";
import { useWorkflowsStore } from "../stores/workflows";
import { useEleventEventStore } from "../stores/elementEvent";
import {
  extractElementProperties,
  generateEventHandlerPrompt,
  getAllFormElements,
} from "../utils";

type TabType = 'chat' | 'design' | 'workflow' | null;

/**
 * Custom hook to handle cross-frame communication
 */
export function useIframeMessaging() {
  const [activeTab, setActiveTab] = useState<TabType>(null);
  const [shouldEnableInspect, setShouldEnableInspect] = useState<boolean>(false);
  
  // Effect to listen for messages from the parent window
  useEffect(() => {    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'TAB_CHANGED') {
        const { activeTab: newActiveTab } = event.data.payload || {};
        
        setActiveTab(newActiveTab as TabType);
        
        // Disable inspect mode for chat tab
        if (newActiveTab === 'chat') {
          setShouldEnableInspect(false);
        }
        // Enable inspect mode for design and workflow tabs
        else if (newActiveTab === 'design' || newActiveTab === 'workflow') {
          setShouldEnableInspect(true);
        }
      } else if (event.data?.type === 'WORKFLOW_LIST') {
        useWorkflowsStore.getState().setWorkflows(event.data.workflows);
      } else if (event.data?.type === 'ELEMENT_INSPECTOR_UPDATE') {
        const { target, text, style, src, element } = event.data;
        
        // Find element with matching data-simplify-id
        if (target) {
          const targetElement = document.querySelector(`[data-simplify-id="${target}"]`) as HTMLElement;
          
          if (targetElement) {
            // Replace HTML if element is provided
            if (element !== undefined) {
              const allTargetElements = document.querySelectorAll(`[data-simplify-id="${target}"]`);
              allTargetElements.forEach((el) => {
                (el as HTMLElement).innerHTML = element;
              });
            }
            
            // Update text content if provided
            if (text !== undefined) {
              targetElement.textContent = text;
            }
            
            // Update src attribute if provided (for img, iframe, video, audio, etc.)
            if (src !== undefined) {
              targetElement.setAttribute('src', src);
            }
            
            // Apply styles if provided
            if (style && typeof style === 'object') {
              // Find all elements with the same data-simplify-id
              const allTargetElements = document.querySelectorAll(`[data-simplify-id="${target}"]`);
              
              allTargetElements.forEach((element) => {
                const el = element as HTMLElement;
                Object.entries(style).forEach(([key, value]) => {
                  if (value === 'default') {
                    // Remove the style property if value is "default"
                    el.style.removeProperty(kebabCase(key));
                  } else {
                    // Apply the style
                    el.style[key as any] = value as string;
                  }
                });
              });
            }
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };  }, [activeTab]);

  // Send selected elements to parent window
  const sendSelectedElements = useCallback((elements: HTMLElement[]) => {
    window.parent.postMessage({
      type: 'ELEMENT_INSPECTOR_SELECTED',
      payload: {
        referenceInputs: getAllFormElements(),
        elements: elements.map(el => extractElementProperties(el))
      }
    }, '*');
  }, []);
  
  // Send generated prompt to parent window
  const sendPrompt = useCallback((prompt: string, elements: HTMLElement[]) => {
    let finalPrompt = prompt
    const workflowId = useWorkflowsStore.getState().workflowId
    if (!!workflowId && !!prompt) {
      const { selectedEvent, selectedAction } = useEleventEventStore.getState();
      finalPrompt += generateEventHandlerPrompt({
        event: selectedEvent,
        action: selectedAction,
        elements
      })
    }
    
    window.parent.postMessage({
      type: 'ELEMENT_INSPECTOR_PROMPT',
      payload: {
        workflowId,
        prompt: finalPrompt,
        activeTab,
        referenceInputs: getAllFormElements(),
        elements: elements.map(el => extractElementProperties(el))
      }
    }, '*');
  }, [activeTab]);
  
  return {
    sendSelectedElements,
    sendPrompt,
    activeTab,
    shouldEnableInspect
  };
}
